/* Native Shopify discounts set every price. Draft cart automatically adds an eligible optional gift; shoppers can opt out. */
(() => {
  if (customElements.get('be-canada-gift')) return;
  let busy = false;
  const preferenceKey = 'be-ca-gift-opt-out';
  const attemptKey = 'be-ca-gift-attempt';
  const memoryPreferences = new Map();
  const readPreference = key => { try { return sessionStorage.getItem(key); } catch (_) { return memoryPreferences.get(key) || ''; } };
  const savePreference = (key, value) => { memoryPreferences.set(key, value); try { sessionStorage.setItem(key, value); } catch (_) {} };
  const fingerprint = panel => [panel.dataset.code, panel.dataset.country, panel.dataset.currency, panel.dataset.qualifying].join('|');
  const root = () => window.Shopify?.routes?.root || '/';
  const marked = item => item.properties?._be_ca_gift === '65';
  async function request(path, body) {
    const response = await fetch(root() + path, body === undefined ? { cache: 'no-store' } : {
      method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body)
    });
    const result = await response.json();
    if (!response.ok) throw new Error(result.description || 'Your cart could not be updated. Please try again.');
    return result;
  }
  function status(message) {
    document.querySelectorAll('be-canada-gift [data-gift-status]').forEach(node => { node.textContent = message; });
  }
  function lock(value) {
    busy = value;
    document.querySelectorAll('[data-gift-claim], [data-kit-add]').forEach(button => { button.disabled = value; });
  }
  function cartURL(panel) {
    const url = new URL(root() + 'cart', location.origin);
    if (panel.dataset.preview) url.searchParams.set('preview_theme_id', panel.dataset.preview);
    return url;
  }
  async function freshPanel(panel) {
    const url = cartURL(panel);
    const response = await fetch(url, { cache: 'no-store' });
    if (!response.ok) throw new Error('Could not check gift eligibility. Please try again.');
    const node = new DOMParser().parseFromString(await response.text(), 'text/html').querySelector('be-canada-gift');
    if (!node) throw new Error('Gift eligibility is unavailable. Your normal checkout is still available.');
    return node;
  }
  function eligible(panel) {
    return panel.dataset.country === 'CA' && panel.dataset.currency === 'CAD' &&
      Number(panel.dataset.qualifying) >= 6450 && panel.dataset.available === 'true' &&
      panel.dataset.otherDiscount !== 'true';
  }
  async function removeMarked(codeToRemove) {
    let cart = await request('cart.js');
    // Re-fetch after each change because Shopify can change line keys after discount allocation.
    while (cart.items.some(marked)) {
      const line = cart.items.find(marked);
      cart = await request('cart/change.js', { id: line.key, quantity: 0 });
    }
    if (codeToRemove) {
      const retained = (cart.discount_codes || []).map(entry => entry.code).filter(code => code && code !== codeToRemove).join(',');
      cart = await request('cart/update.js', { discount: retained });
    }
    return cart;
  }
  class CanadaGift extends HTMLElement {
    connectedCallback() {
      if (this.dataset.itemCount === '0') {
        savePreference(preferenceKey, '');
        savePreference(attemptKey, '');
      }
      this.querySelectorAll('[data-kit-add]').forEach(button => button.addEventListener('click', () => this.addKit(button)));
      this.querySelector('[data-gift-claim]')?.addEventListener('click', () => {
        savePreference(preferenceKey, '');
        this.claim();
      });
      this.maybeAuto();
      if (Number(this.dataset.giftQuantity) > 0 &&
        (!eligible(this) || this.dataset.giftFree !== 'true' || Number(this.dataset.giftQuantity) !== 1)) {
        queueMicrotask(() => this.clean());
      }
    }
    maybeAuto() {
      const drawer = this.closest?.('cart-drawer');
      if ((/\/cart\/?$/.test(location.pathname) || drawer?.classList.contains('active')) && eligible(this) &&
          Number(this.dataset.giftQuantity) === 0 && this.dataset.nativeGift !== 'true' &&
          !readPreference(preferenceKey) && readPreference(attemptKey) !== fingerprint(this)) {
        queueMicrotask(() => {
          if (!busy && this.isConnected) {
            savePreference(attemptKey, fingerprint(this));
            void this.claim();
          }
        });
      }
    }
    async refreshCart() {
      const drawer = this.closest?.('cart-drawer');
      if (!drawer || /\/cart\/?$/.test(location.pathname)) { location.assign(cartURL(this)); return; }
      const url = cartURL(this);
      url.searchParams.set('sections', 'cart-drawer,cart-icon-bubble');
      const response = await fetch(url, {cache:'no-store'});
      const sections = await response.json();
      if (!response.ok || !sections['cart-drawer'] || !sections['cart-icon-bubble']) {
        throw new Error('Your cart was updated. Open the cart page to see the latest totals.');
      }
      drawer.renderContents({sections});
      lock(false);
      drawer.querySelector('be-canada-gift')?.maybeAuto();
    }
    async addKit(button) {
      if (busy) return;
      lock(true);
      const variant = button.closest('.be-kit-card').querySelector('[data-kit-variant]')?.value || button.dataset.kitAdd;
      try {
        await request('cart/add.js', { items: [{ id: Number(variant), quantity: 1 }] });
        await this.refreshCart();
      } catch (error) { status(error.message); lock(false); }
    }
    async removeGift() {
      if (busy) return;
      lock(true);
      status('Removing your optional gift…');
      try {
        await removeMarked(this.dataset.code);
        await this.refreshCart();
      } catch (error) { status(error.message); lock(false); }
    }
    async clean() {
      if (busy || !this.isConnected) return;
      lock(true);
      status('Checking your gift…');
      try {
        const current = await freshPanel(this);
        if (Number(current.dataset.giftQuantity) > 0 &&
          (!eligible(current) || current.dataset.giftFree !== 'true' || Number(current.dataset.giftQuantity) !== 1)) {
          await removeMarked(this.dataset.code);
          await this.refreshCart();
          return;
        }
      } catch (error) { status(error.message + ' Please remove the promotional rope if it is not free.'); }
      lock(false);
    }
    async claim() {
      if (busy) return;
      lock(true);
      status('Checking your Canadian gift…');
      let added = false;
      let previousCodes = '';
      try {
        const current = await freshPanel(this);
        if (!eligible(current) || Number(current.dataset.giftQuantity) > 0 || current.dataset.nativeGift === 'true') {
          throw new Error('Your cart has changed. Refresh the cart to check gift eligibility.');
        }
        const before = await request('cart.js');
        previousCodes = (before.discount_codes || []).map(entry => entry.code).filter(Boolean).join(',');
        if ((before.discount_codes || []).some(entry => entry.applicable && entry.code !== current.dataset.code)) {
          throw new Error('Another discount is applied. The gift cannot be combined with that discount.');
        }
        await request('cart/add.js', { items: [{ id: Number(current.dataset.variant), quantity: 1, properties: { _be_ca_gift: '65' } }] });
        added = true;
        await request('cart/update.js', { discount: current.dataset.code });
        const checked = await freshPanel(this);
        if (!eligible(checked) || checked.dataset.giftFree !== 'true' || Number(checked.dataset.giftQuantity) !== 1) {
          throw new Error('The gift discount could not be applied. No paid gift will be kept in your cart.');
        }
        await this.refreshCart();
        // Only failed attempts stay suppressed; a later eligible visit can earn a gift again.
        savePreference(attemptKey, '');
      } catch (error) {
        if (added) {
          try {
            await removeMarked();
            await request('cart/update.js', { discount: previousCodes });
          } catch (_) {
            status('The cart could not be restored. Open your cart and remove the promotional rope before checkout if it has a price.');
            lock(false);
            return;
          }
        }
        status(error.message);
        lock(false);
      }
    }
  }
  customElements.define('be-canada-gift', CanadaGift);
  window.addEventListener('be:drawer-open', () => {
    document.querySelectorAll('cart-drawer.active be-canada-gift').forEach(panel => panel.maybeAuto());
  });
  // Capture the customer's own remove action before Dawn rerenders the cart.
  window.addEventListener('click', event => {
    if (!event.target.closest('[data-be-gift-remove]')) return;
    savePreference(preferenceKey, 'yes');
    const drawer = event.target.closest('cart-drawer');
    const panel = drawer?.querySelector?.('be-canada-gift') || document.querySelector('be-canada-gift');
    if (panel?.removeGift) {
      event.preventDefault();
      event.stopImmediatePropagation();
      void panel.removeGift();
    }
  }, true);
  // Guard standard checkout against a stale or no-longer-free promotional line.
  async function guardCheckout(event) {
    const button = event.submitter || event.target.closest('button[name="checkout"], input[name="checkout"]');
    const panel = document.querySelector('be-canada-gift');
    if (!button || button.name !== "checkout" || !panel) return;
    if (busy) { event.preventDefault(); event.stopImmediatePropagation(); return; }
    if (!Number(panel.dataset.giftQuantity)) return;
    event.preventDefault(); event.stopImmediatePropagation();
    lock(true);
    try {
      const current = await freshPanel(panel);
      if (!eligible(current) || current.dataset.giftFree !== 'true' || Number(current.dataset.giftQuantity) !== 1) {
        await removeMarked(panel.dataset.code);
        location.assign(cartURL(panel));
      } else {
        location.assign(root() + 'checkout');
      }
    } catch (error) { status(error.message); lock(false); }
  }
  window.addEventListener('click', guardCheckout, true);
  window.addEventListener('submit', guardCheckout, true);
})();
